import React, { useState } from 'react';

import { cn } from '@/libs';

import { uiConfig } from '@/config';

import { GroupTitle, FormFieldMapper } from '..';
import { Checkbox } from '../inputs';
import { GroupRestrictionErrors } from './GroupRestrictionErrors';
import { GroupRestrictionHint } from './GroupRestrictionHint';
import {
  useAddressChoice,
  useClearOnNotApplicable,
  useEffectiveAddress,
  useNotApplicableField,
  useShouldValidateField,
  useGroupRestrictionError,
  useFieldDeclarations,
} from '../../hooks';
import type { FieldsGroupFormField } from '../../typings';
import { AddressSourceChoice } from './AddressSourceChoice';
import { FieldDeclarationAlert } from './FieldDeclarationAlert';
import { FormFieldAlertMessage } from './FormFieldAlertMessage';

import './FieldsGroupPresenter.scss';

type FieldsGroupPresenterProps = {
  fieldsGroup: FieldsGroupFormField;
};

/**
 * Renders a form group presenter component.
 *
 * @internal
 * @component
 * @example
 * ```tsx
 * <FormGroupPresenter fieldGroup={fieldGroup} />
 * ```
 *
 * @param props - The component props.
 * @param props.fieldGroup - The field group object.
 * @returns The rendered component.
 */
export const FieldsGroupPresenter: React.FC<
  React.PropsWithChildren<FieldsGroupPresenterProps>
> = ({ fieldsGroup }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const hasRestrictionError = useGroupRestrictionError(
    fieldsGroup.fieldId,
    fieldsGroup.restriction,
  );

  const containerClasses = cn(
    'fg__container',
    uiConfig.grid.maxColumnsSpanClass,
    {
      'fg__container--collapsed': isCollapsed,
      'fg__container--error': hasRestrictionError,
    },
  );

  const childrenClasses = cn(
    'grid',
    'gap-x-[20px]',
    'gap-y-[35px]',
    uiConfig.grid.columnsClass,
    'fg__row',
    {
      'fg__row--collapsed': isCollapsed,
    },
  );

  const isFieldNA = useNotApplicableField(
    fieldsGroup.notApplicableField?.fieldId,
  );

  const choice = useAddressChoice(fieldsGroup.fieldId, fieldsGroup.address);
  const effectiveAddress = useEffectiveAddress(
    fieldsGroup.fieldId,
    fieldsGroup.address,
  );

  // A required address choice holds every field back until it is answered.
  useShouldValidateField(
    fieldsGroup.fieldId,
    !isFieldNA && !choice.hidesAllFields,
  );
  useClearOnNotApplicable(fieldsGroup, isFieldNA);

  const declarations = useFieldDeclarations(fieldsGroup.fields);

  const visibleFields = choice.hidesAllFields
    ? []
    : fieldsGroup.fields.filter(
        field => !choice.hiddenFieldIds.has(field.fieldId),
      );

  return (
    // data-jump-scroll-only: when GroupTitle renders, it may carry a collapse
    // toggle (only when isCollapsible is true) — a title without controls has
    // no focusable descendant itself, so a failed submit would land on a genuine
    // field below. Only gate the attribute when the title actually carries a
    // focusable control — that's the same condition as when GroupTitle renders
    // at all (fieldsGroup.name).
    <div
      id={fieldsGroup.fieldId}
      className={containerClasses}
      data-jump-scroll-only={fieldsGroup.name ? 'true' : undefined}
    >
      {fieldsGroup.name && (
        <GroupTitle
          title={fieldsGroup.name}
          subtitle={isCollapsed ? effectiveAddress : undefined}
          isCollapsible={fieldsGroup.isCollapsible}
          isCollapsed={isCollapsed}
          onCollapse={() => setIsCollapsed(prev => !prev)}
        />
      )}
      <GroupRestrictionHint restriction={fieldsGroup.restriction} />
      {fieldsGroup.alertMessage && (
        <FormFieldAlertMessage alertMessage={fieldsGroup.alertMessage} />
      )}

      {fieldsGroup.notApplicableField !== undefined && (
        <Checkbox
          name={fieldsGroup.notApplicableField.fieldId}
          label={fieldsGroup.notApplicableField.label}
          size="small"
        />
      )}

      {isFieldNA && fieldsGroup.naDetails && (
        <FormFieldMapper field={fieldsGroup.naDetails} disabled={false} />
      )}

      {(choice.showsChoice || visibleFields.length > 0) && (
        <div className={childrenClasses}>
          {choice.showsChoice && fieldsGroup.address && (
            <AddressSourceChoice
              groupFieldId={fieldsGroup.fieldId}
              groupTitle={fieldsGroup.name}
              address={fieldsGroup.address}
              choice={choice}
              disabled={isFieldNA}
            />
          )}
          {visibleFields.map(field => (
            <React.Fragment key={field.fieldId}>
              <FormFieldMapper field={field} disabled={isFieldNA} />
              {!isFieldNA &&
                declarations
                  .filter(d => d.anchorFieldId === field.fieldId)
                  .map(d => <FieldDeclarationAlert key={d.message} message={d.message} />)}
            </React.Fragment>
          ))}
        </div>
      )}

      <GroupRestrictionErrors
        groupFieldId={fieldsGroup.fieldId}
        restriction={fieldsGroup.restriction}
        fields={fieldsGroup.fields}
        hintAbove
      />
    </div>
  );
};
